import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import api from '../utils/api';
import '../styles/global.css';

export default function Settings() {
  const { client, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [notifications, setNotifications] = useState({ email_notifications: true, sms_notifications: true, expiry_reminders: true, family_sharing: true });
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/account').then(res => {
      setForm({ first_name: res.data.first_name, last_name: res.data.last_name, phone: res.data.phone || '' });
      setNotifications({
        email_notifications: res.data.email_notifications,
        sms_notifications: res.data.sms_notifications,
        expiry_reminders: res.data.expiry_reminders,
        family_sharing: res.data.family_sharing
      });
    }).catch(console.error);
  }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const saveDetails = async () => {
    setLoading(true);
    try { await api.put('/account', form); showToast('Personal details saved!'); }
    catch { showToast('Failed to save. Please try again.'); }
    finally { setLoading(false); }
  };

  const savePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) { showToast('Passwords do not match.'); return; }
    setLoading(true);
    try { await api.put('/account/password', passwords); showToast('Password updated!'); setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' }); }
    catch { showToast('Current password is incorrect.'); }
    finally { setLoading(false); }
  };

  const saveNotifications = async () => {
    try { await api.put('/account', notifications); showToast('Notification preferences saved!'); }
    catch { showToast('Failed to save.'); }
  };

  const Toggle = ({ value, onChange }) => (
    <div onClick={onChange} style={{ width: '44px', height: '24px', background: value ? 'var(--teal)' : 'var(--border)', borderRadius: '100px', position: 'relative', cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s' }}>
      <div style={{ position: 'absolute', width: '18px', height: '18px', background: 'white', borderRadius: '50%', top: '3px', left: value ? 'auto' : '3px', right: value ? '3px' : 'auto', transition: 'all 0.2s' }} />
    </div>
  );

  const Section = ({ title, children }) => (
    <div style={{ background: 'white', borderRadius: '18px', border: '1px solid var(--border)', overflow: 'hidden', marginBottom: '20px', boxShadow: '0 2px 12px rgba(83,147,144,0.08)' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', background: 'var(--teal-light)' }}>
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '12px', letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-soft)' }}>{title}</div>
      </div>
      <div style={{ padding: '24px' }}>{children}</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="app-header">
        <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '20px', color: 'var(--teal)' }}>
          BUMP <span style={{ color: '#F68F7C' }}>&#x2665;&#xFE0E;</span> BABY IMAGING
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => navigate('/')} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>← My Sessions</button>
          <button onClick={logout} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '100px', padding: '6px 14px', fontSize: '12px', fontWeight: 700, color: 'var(--text-soft)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif' }}>Log out</button>
        </div>
      </header>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '32px 24px', width: '100%', flex: 1 }}>
        <div style={{ marginBottom: '28px' }}>
          <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: '24px', color: 'var(--teal-dark)' }}>Account Settings</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-soft)', marginTop: '4px' }}>Manage your personal details and preferences</p>
        </div>

        <Section title="Personal Details">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>First Name</label>
              <input value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Last Name</label>
              <input value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
          </div>
          <div className="form-group">
            <label>Email Address</label>
            <input type="email" value={client?.email || ''} readOnly style={{ background: 'var(--teal-light)', color: 'var(--text-soft)' }} />
          </div>
          <div className="form-group">
            <label>Phone Number</label>
            <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="0412 345 678" />
          </div>
          <button onClick={saveDetails} disabled={loading} style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Save Changes</button>
        </Section>

        <Section title="Password">
          <div className="form-group">
            <label>Current Password</label>
            <input type="password" placeholder="Enter current password" value={passwords.currentPassword} onChange={e => setPasswords(p => ({ ...p, currentPassword: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>New Password</label>
            <input type="password" placeholder="At least 8 characters" value={passwords.newPassword} onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>Confirm New Password</label>
            <input type="password" placeholder="Repeat new password" value={passwords.confirmPassword} onChange={e => setPasswords(p => ({ ...p, confirmPassword: e.target.value }))} />
          </div>
          <button onClick={savePassword} disabled={loading} style={{ background: 'var(--coral)', color: 'white', border: 'none', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer' }}>Update Password</button>
        </Section>

        <Section title="Notifications">
          {[
            { key: 'email_notifications', title: 'Email notifications', desc: 'Get an email when your gallery is updated' },
            { key: 'sms_notifications', title: 'SMS notifications', desc: 'Get a text when new files are ready' },
            { key: 'expiry_reminders', title: 'Expiry reminders', desc: 'Get notified before your gallery files are removed' },
            { key: 'family_sharing', title: 'Family sharing', desc: 'Allow family members to be invited to your gallery' }
          ].map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: '1px solid var(--teal-light)' }}>
              <div>
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--teal-dark)', fontFamily: 'Nunito, sans-serif' }}>{item.title}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-soft)', marginTop: '2px' }}>{item.desc}</div>
              </div>
              <Toggle value={notifications[item.key]} onChange={() => { setNotifications(n => ({ ...n, [item.key]: !n[item.key] })); setTimeout(saveNotifications, 100); }} />
            </div>
          ))}
        </Section>

        <Section title="Account">
          <button onClick={async () => { if (window.confirm('Are you sure you want to delete your account? This cannot be undone.')) { await api.delete('/account'); logout(); navigate('/login'); } }} style={{ background: 'none', border: '2px solid #ffcccc', color: '#cc4444', borderRadius: '10px', padding: '11px 24px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', cursor: 'pointer', width: '100%' }}>
            Delete My Account
          </button>
        </Section>
      </div>

      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'var(--teal-dark)', color: 'white', padding: '12px 24px', borderRadius: '100px', fontSize: '14px', fontWeight: 700, fontFamily: 'Nunito, sans-serif', boxShadow: '0 8px 24px rgba(45,100,100,0.3)', zIndex: 300 }}>
          {toast}
        </div>
      )}

      <footer className="app-footer">
        <p>Made with <span className="heart">&#x2665;&#xFE0E;</span> by <strong>Bump Baby Imaging</strong></p>
      </footer>
    </div>
  );
}
